/**
 * Web karşılığı: iOS HopsDatePolicy — depolama ISO 8601 UTC, gösterim TR / Europe/Istanbul.
 */

const IST_OFFSET = '+03:00'; // Türkiye sabit UTC+3 (2016 sonrası; vanilla JS ile hizalı)

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** Yerel İstanbul bileşenlerinden anlık UTC Date (sabit +03). */
function dateFromIstanbulParts(year, month, day, hour, minute, second) {
  const iso = `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}:${pad2(second)}${IST_OFFSET}`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function currentYearIstanbul() {
  const y = new Intl.DateTimeFormat('en', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
  }).format(new Date());
  return parseInt(y, 10);
}

/**
 * Firestore veya başka kaynaklardan gelen eski/yeni formatlı tarih stringini parse eder.
 * @param {string} s
 * @returns {Date | null}
 */
export function parseFlexible(s) {
  if (s == null || typeof s !== 'string') return null;
  const trimmed = s.trim();
  if (!trimmed) return null;

  // 1) ISO 8601 datetime (T ile; tarih-saat)
  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    const t = Date.parse(trimmed);
    if (!Number.isNaN(t)) return new Date(t);
    return null;
  }

  // 2) dd.MM.yyyy HH:mm
  let m = /^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2})$/.exec(trimmed);
  if (m) {
    const d = dateFromIstanbulParts(+m[3], +m[2], +m[1], +m[4], +m[5], 0);
    if (d) return d;
  }

  // 3) dd.MM.yyyy
  m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(trimmed);
  if (m) {
    const d = dateFromIstanbulParts(+m[3], +m[2], +m[1], 0, 0, 0);
    if (d) return d;
  }

  // 4) yyyy-MM-dd
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (m) {
    const d = dateFromIstanbulParts(+m[1], +m[2], +m[3], 0, 0, 0);
    if (d) return d;
  }

  // 5) yyyy-MM-dd HH:mm:ss
  m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(trimmed);
  if (m) {
    const d = dateFromIstanbulParts(+m[1], +m[2], +m[3], +m[4], +m[5], +m[6]);
    if (d) return d;
  }

  // 6) Firestore Timestamp string — "… at …" kaldırıp best-effort
  if (/\s+at\s+/i.test(trimmed)) {
    const cleaned = trimmed.replace(/\s+at\s+/gi, ' ');
    const fd = new Date(cleaned);
    if (!Number.isNaN(fd.getTime())) return fd;
  }

  // 7) dd/MM — mevcut (İstanbul) yılı
  m = /^(\d{1,2})\/(\d{1,2})$/.exec(trimmed);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const year = currentYearIstanbul();
    const d = dateFromIstanbulParts(year, month, day, 0, 0, 0);
    if (d) return d;
  }

  return null;
}

/**
 * @param {Date} d
 * @returns {string}
 */
export function toIsoUtcString(d) {
  return d.toISOString();
}

const EM_DASH = '\u2014';

function toValidDate(input) {
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  if (typeof input === 'string') return parseFlexible(input);
  return null;
}

function formatTrParts(date, includeTime) {
  const opts = {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  if (includeTime) {
    opts.hour = '2-digit';
    opts.minute = '2-digit';
    opts.hour12 = false;
  }
  const parts = new Intl.DateTimeFormat('en-GB', opts).formatToParts(date);
  const map = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  const day = map.day;
  const month = map.month;
  const year = map.year;
  if (!day || !month || !year) return null;
  const base = `${day}.${month}.${year}`;
  if (!includeTime) return base;
  const hour = map.hour;
  const minute = map.minute;
  if (hour == null || minute == null) return null;
  return `${base} ${hour}:${minute}`;
}

/**
 * @param {Date | string} input
 * @returns {string}
 */
export function formatTrDateTime(input) {
  const d = toValidDate(input);
  if (!d) return EM_DASH;
  const s = formatTrParts(d, true);
  return s ?? EM_DASH;
}

/**
 * @param {Date | string} input
 * @returns {string}
 */
export function formatTrDateOnly(input) {
  const d = toValidDate(input);
  if (!d) return EM_DASH;
  const s = formatTrParts(d, false);
  return s ?? EM_DASH;
}
